import React from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    CircularProgress,
} from "@mui/material";
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    SettingsInputAntenna as RFTestIcon,
    LocationOn as LocationIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { useThemeMode } from "../../../contexts/ThemeContext";
import { getMagnetIcon } from "./magnetIcons";

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MM/dd/yyyy");
    } catch {
        return dateStr;
    }
}

function linkedLabel(report) {
    if (report.project_detail) {
        return `${report.project_detail.number} · ${report.project_detail.name}`;
    }
    if (report.quote_detail) {
        return `${report.quote_detail.number} · ${report.quote_detail.name}`;
    }
    return "—";
}

export default function RFTestReportsList({
    reports,
    loading,
    onView,
    onEdit,
    onDelete,
}) {
    const { mode } = useThemeMode();
    const isDark = mode === "dark";

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (reports.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <RFTestIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">
                    No RF Test Reports found.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {reports.map((report) => {
                const MagnetIcon = getMagnetIcon(report.magnet_type, isDark);
                return (
                    <Card
                        key={report.id}
                        sx={{
                            position: "relative",
                            boxShadow: "none",
                            border: 1,
                            borderColor: "primary.main",
                        }}
                    >
                        <ChevronRightIcon
                            sx={{
                                position: "absolute",
                                top: "50%",
                                right: 8,
                                transform: "translateY(-50%)",
                                color: "text.disabled",
                                pointerEvents: "none",
                            }}
                        />
                        <CardActionArea
                            onClick={() => onView(report)}
                            sx={{
                                cursor: "pointer",
                                "&:hover": {
                                    bgcolor: isDark
                                        ? "rgba(255, 255, 255, 0.06)"
                                        : "rgba(0, 0, 0, 0.04)",
                                },
                            }}
                        >
                            <CardContent
                                sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            sx={{
                                                fontFamily:
                                                    "Montserrat, sans-serif",
                                            }}
                                        >
                                            {report.report_number}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            {linkedLabel(report)}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 0.5,
                                                mt: 0.75,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {report.location && (
                                                <Chip
                                                    icon={<LocationIcon />}
                                                    label={report.location}
                                                    size="small"
                                                    color="secondary"
                                                />
                                            )}
                                            {report.test_type && (
                                                <Chip
                                                    label={report.test_type}
                                                    size="small"
                                                />
                                            )}
                                            {/* {report.magnet_type && (
                                            <Chip
                                                label={report.magnet_type}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )} */}
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ mt: 0.5 }}
                                        >
                                            {formatDate(report.created)}
                                            {report.created_by_detail
                                                ? ` · ${report.created_by_detail.full_name || report.created_by_detail.username}`
                                                : ""}
                                        </Typography>
                                    </Box>
                                    {MagnetIcon && (
                                        <Box
                                            sx={{
                                                flexShrink: 0,
                                                // width: 56,
                                                // height: 10,
                                                width: 100, // Set a fixed width for the MagnetIcon container
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                "& svg": {
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                    width: "auto",
                                                    height: "auto",
                                                },
                                            }}
                                        >
                                            <MagnetIcon />
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </CardActionArea>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                px: 1,
                                pb: 1,
                                gap: 0.5,
                            }}
                        >
                            <Tooltip title="View">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => onView(report)}
                                >
                                    <ViewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ mx: 0.25 }}
                            />
                            <Tooltip title="Edit">
                                <IconButton
                                    size="small"
                                    onClick={() => onEdit(report)}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ mx: 0.25 }}
                            />
                            <Tooltip title="Delete">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onDelete(report)}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Card>
                );
            })}
        </Box>
    );
}
